import {MESSAGE_MODALITIES} from '@constants/communicator';

/**
 * Comparator for sorting rank keys (e.g. 'rank1', 'rank2', 'rank10') by their numeric portion.
 */
const compareRankKeys = (a, b) => Number.parseInt(a.replace('rank', '')) - parseInt(b.replace('rank', ''));

/**
 * Creates unique delivery method IDs from modalityLabels object by combining modality with the label name
 * @param {Object} modalityLabels - Object mapping modality to array of labels
 * @example
 * const modalityLabels = {
 *   "emailFormat": ["personal", "work"],
 *   "smsFormat": ["personal"],
 *   "ttsFormat": [],
 *   "pushFormat": []
 * }
 * getDeliveryMethodRankingIdsFromLabels(modalityLabels)
 * // Returns: ["emailFormat-personal", "emailFormat-work", "smsFormat-personal", "pushFormat"]
 *
 * @returns {string[]} - Array of delivery method IDs
 */
export function getDeliveryMethodRankingIdsFromLabels(modalityLabels) {
  const ids = [];
  Object.entries(modalityLabels).forEach(([modality, labels]) => {
    // For modalities without labels (push, desktop), just use the modality name as ID
    if (!labels || labels.length === 0) {
      ids.push(modality);
    } else {
      // For modalities with labels, create composite IDs based on modality+label name
      labels.forEach((label) => {
        ids.push(`${modality}-${label}`);
      });
    }
  });
  return ids;
}

/**
 * Mapping from API channel type to internal modality key
 */
const API_TYPE_TO_MODALITY = {
  EMAIL: MESSAGE_MODALITIES.EMAIL_FORMAT,
  SMS: MESSAGE_MODALITIES.SMS_FORMAT,
  TTS: MESSAGE_MODALITIES.TTS_FORMAT,
  PUSH: MESSAGE_MODALITIES.PUSH_FORMAT,
  DESKTOP_ALERT: MESSAGE_MODALITIES.DESKTOP_FORMAT,
};

/**
 * Mapping from internal modality key to API channel type
 */
const MODALITY_TO_API_TYPE = {
  [MESSAGE_MODALITIES.EMAIL_FORMAT]: 'EMAIL',
  [MESSAGE_MODALITIES.SMS_FORMAT]: 'SMS',
  [MESSAGE_MODALITIES.TTS_FORMAT]: 'TTS',
  [MESSAGE_MODALITIES.PUSH_FORMAT]: 'PUSH',
  [MESSAGE_MODALITIES.DESKTOP_FORMAT]: 'DESKTOP_ALERT',
};

/**
 * Converts an API deliveryPriorityRule object to the internal rankingItems format used by the form/view.
 *
 * @param {Object} deliveryPriorityRule - API format delivery priority rule
 * @param {string} deliveryPriorityRule.waitBetweenLevels - ISO 8601 duration string (e.g. "PT5M")
 * @param {Array} deliveryPriorityRule.priorities - Array of priority level objects
 * @returns {{waitTime: string, rankingItems: Object}|null} Internal format, or null if no rule provided
 * @example
 * deliveryPriorityRuleToRankingItems({
 *   waitBetweenLevels: 'PT5M',
 *   priorities: [{level: 1, channels: [{type: 'EMAIL', label: 'PERSONAL'}, {type: 'PUSH'}]}]
 * })
 * // Returns: {waitTime: 'PT5M', rankingItems: {rank1: ['emailFormat-PERSONAL', 'pushFormat']}}
 */
export function deliveryPriorityRuleToRankingItems(deliveryPriorityRule) {
  if (!deliveryPriorityRule?.priorities) return null;

  const rankingItems = {};
  [...deliveryPriorityRule.priorities]
    .sort((a, b) => a.level - b.level)
    .forEach((priority) => {
      rankingItems[`rank${priority.level}`] = priority.channels.map((channel) => {
        const modality = API_TYPE_TO_MODALITY[channel.type];
        //l Label may not exist for some modalities, such as desktop or push. We just use modality name itself in this case
        return channel.label ? `${modality}-${channel.label}` : modality;
      });
    });

  return {
    rankingItems,
    waitTime: deliveryPriorityRule.waitBetweenLevels,
  };
}

/**
 * Converts an internal item ID to an API channel object.
 * Item IDs are either a plain modality key (e.g. 'pushFormat') or a composite 'modality-LABEL'
 * string (e.g. 'emailFormat-PERSONAL'). The label portion is extracted and included only when present.
 *
 * @param {string} itemId - Internal item ID (e.g. 'emailFormat-PERSONAL' or 'pushFormat')
 * @returns {{type: string, label?: string}} API channel object (e.g. {type: 'EMAIL', label: 'PERSONAL'})
 */
const itemIdToChannel = (itemId) => {
  const modality = Object.values(MESSAGE_MODALITIES).find((key) => itemId === key || itemId.startsWith(`${key}-`));
  //finds the part of the string corresponding to what is after the "-" for given modality, so "emailFormat-PERSONAL" returns "PERSONAL".
  const label = modality && itemId !== modality ? itemId.slice(modality.length + 1) : undefined;
  return {
    type: MODALITY_TO_API_TYPE[modality],
    label,
  };
};

/**
 * Converts the internal rankingItems format to an API deliveryPriorityRule object.
 * Empty ranks are excluded. Remaining ranks are renumbered consecutively from level 1.
 *
 * @param {Object} rankingItems - Internal format: {rank1: ['emailFormat-PERSONAL', 'pushFormat'], rank2: [], ...}
 * @param {string} waitTime - ISO 8601 duration string (e.g. "PT5M")
 * @returns {Object} API format deliveryPriorityRule
 * @example
 * rankingItemsToDeliveryPriorityRule({rank1: ['emailFormat-PERSONAL', 'pushFormat'], rank2: []}, 'PT5M')
 * // Returns: {waitBetweenLevels: 'PT5M', priorities: [{level: 1, channels: [{type: 'EMAIL', label: 'PERSONAL'}, {type: 'PUSH'}]}]}
 */
export function rankingItemsToDeliveryPriorityRule(rankingItems, waitTime) {
  const priorities = Object.entries(rankingItems)
    .filter(([, items]) => items.length > 0)
    .sort(([a], [b]) => compareRankKeys(a, b))
    .map(([, itemIds], index) => ({level: index + 1, channels: itemIds.map(itemIdToChannel)}));
  return {waitBetweenLevels: waitTime, priorities};
}

/**
 * Merges delivery method IDs into an existing ranking structure
 * Filters each rank to only include the provided IDs, and adds any new IDs to the first rank (if existingRanking is not given, everything goes into the first rank)
 *
 * @param {string[]} deliveryMethodIds - Array of delivery method IDs that should be included
 * @param {Object} existingRanking - Existing ranking structure (e.g., {rank1: [], rank2: [], rank3: []})
 * @example
 * mergeIdsIntoRanking(['email-personal', 'email-work'], {rank1: ['email-personal'], rank2: ['sms-mobile'], rank3: []})
 * // Returns: {rank1: ['email-personal', 'email-work'], rank2: [], rank3: []}
 *
 * @returns {Object} - New ranking structure with filtered and merged IDs
 */
export function mergeIdsIntoRanking(deliveryMethodIds, existingRanking) {
  const filtered = {};
  const sortedKeys = Object.keys(existingRanking).sort(compareRankKeys);
  const firstRankKey = sortedKeys[0];

  Object.entries(existingRanking).forEach(([rankKey, items]) => {
    const filteredItems = items?.filter((id) => deliveryMethodIds.includes(id)) || [];

    if (rankKey === firstRankKey) {
      const existingIds = new Set(Object.values(existingRanking).flat());
      const newIds = deliveryMethodIds.filter((id) => !existingIds.has(id));
      filtered[rankKey] = [...filteredItems, ...newIds];
    } else {
      filtered[rankKey] = filteredItems;
    }
  });

  return filtered;
}

/**
 * Re-numbers rank keys sequentially (rank1, rank2, ...) to remove gaps.
 * e.g. {rank1: [...], rank3: [...]} → {rank1: [...], rank2: [...]}
 *
 * Why: When delivery methods are removed from a saved ranking, their rank slot becomes empty,
 * leaving gaps (e.g. rank1, rank3). The backend api and delivery ranking modal both expect contiguous rank numbering,
 * so we re-index the keys to be sequential before using the ranking elsewhere.
 */
export function reindexRankKeys(ranking) {
  const sorted = Object.keys(ranking).sort(compareRankKeys);
  const result = {};
  sorted.forEach((key, index) => {
    result[`rank${index + 1}`] = [...ranking[key]];
  });
  return result;
}

export function sortRankKeys(ranking) {
  const result = {};
  // Sort by the numeric portion of the key (e.g. rank2 before rank10)
  Object.keys(ranking)
    .sort(compareRankKeys)
    .forEach((key) => {
      result[key] = ranking[key];
    });
  return result;
}

/**
 * Inserts ALL delivery method IDs from defaultRanking that are missing from ranking.
 * New items are placed in the same rank position the org config assigns them.
 *
 * @param {Object} ranking - The user's current ranking (already reindexed), e.g. {rank1: ["emailFormat-personal"], rank2: ["smsFormat-mobile"]}
 * @param {Object} defaultRanking - The org's default ranking, e.g. {rank1: ["emailFormat-personal"], rank2: ["smsFormat-mobile"], rank3: ["ttsFormat-mobile"]}
 * @returns {Object} - ranking with any missing items from defaultRanking appended to their respective rank
 *
 * @example
 * // User saved: email rank1, sms rank2. Then toggles on voice (org default has it at rank3).
 * insertMissingDefaults(
 *   {rank1: ["emailFormat-personal"], rank2: ["smsFormat-mobile"]},
 *   {rank1: ["emailFormat-personal"], rank2: ["smsFormat-mobile"], rank3: ["ttsFormat-mobile"]}
 * )
 * // Returns: {rank1: ["emailFormat-personal"], rank2: ["smsFormat-mobile"], rank3: ["ttsFormat-mobile"]}
 */
export function insertMissingDefaults(ranking, defaultRanking) {
  // Collect all IDs already present in the ranking — these don't need to be inserted
  const existingIds = new Set(Object.values(ranking).flat());
  const result = {...ranking};

  //For each rank in the org config, find items the user doesn't have yet and append them at that rank"
  Object.keys(defaultRanking).forEach((rankKey) => {
    const newItems = (defaultRanking[rankKey] || []).filter((id) => !existingIds.has(id));
    if (newItems.length > 0) {
      result[rankKey] = [...(result[rankKey] || []), ...newItems];
    }
  });

  return result;
}
